import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Eye,
  FileJson,
  FilePenLine,
  LogOut,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import useDocumentMeta from "../hooks/useDocumentMeta";
import {
  createBlogPost,
  deleteAdminBlogPost,
  fetchAdminBlogPosts,
  updateAdminBlogPost,
} from "../lib/api";
import { clearAdminToken } from "../lib/adminAuth";
import { BLOG_TEMPLATES, getTemplateMeta } from "../lib/blogTemplates";

function createEmptySection() {
  return {
    title: "",
    content: "",
    image_url: "",
    bullets_text: "",
  };
}

const initialForm = {
  title: "",
  seo_title: "",
  seo_description: "",
  excerpt: "",
  template: "listicle",
  category: "",
  hero_image: "",
  hero_image_alt: "",
  supporting_gallery_text: "",
  tags_text: "",
  read_time: "",
  author: "FindClarity Team",
  featured: false,
  published: false,
};

function normalizeTemplate(template) {
  return BLOG_TEMPLATES.some((item) => item.id === template) ? template : "listicle";
}

function normalizeImportedSections(value) {
  if (!Array.isArray(value)) {
    return [createEmptySection(), createEmptySection()];
  }

  const normalized = value.map((section) => ({
    title: String(section?.title || ""),
    content: String(section?.content || ""),
    image_url: String(section?.image_url || ""),
    bullets_text: Array.isArray(section?.bullets)
      ? section.bullets.join("\n")
      : String(section?.bullets_text || section?.bullets || ""),
  }));

  return normalized.length > 0 ? normalized : [createEmptySection(), createEmptySection()];
}

function applyJsonDraftToForm(data) {
  return {
    title: String(data?.title || ""),
    seo_title: String(data?.seo_title || ""),
    seo_description: String(data?.seo_description || ""),
    excerpt: String(data?.excerpt || ""),
    template: normalizeTemplate(String(data?.template || "listicle")),
    category: String(data?.category || ""),
    hero_image: String(data?.hero_image || ""),
    hero_image_alt: String(data?.hero_image_alt || ""),
    supporting_gallery_text: Array.isArray(data?.supporting_gallery)
      ? data.supporting_gallery.join("\n")
      : String(data?.supporting_gallery_text || ""),
    tags_text: Array.isArray(data?.tags)
      ? data.tags.join(", ")
      : String(data?.tags_text || ""),
    read_time: String(data?.read_time || ""),
    author: String(data?.author || "FindClarity Team"),
    featured: Boolean(data?.featured),
    published: Boolean(data?.published),
  };
}

function formatDate(value) {
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const BlogDashboard = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [sections, setSections] = useState([createEmptySection(), createEmptySection()]);
  const [jsonDraft, setJsonDraft] = useState("");
  const [blogPosts, setBlogPosts] = useState([]);
  const [postFilter, setPostFilter] = useState("all");
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [createdPost, setCreatedPost] = useState(null);

  useDocumentMeta({
    title: "FindClarity Blog Dashboard",
    description:
      "Create and publish new FindClarity blog articles with hero images, supporting visuals, and repeatable listicle sections.",
  });

  useEffect(() => {
    loadAdminPosts();
  }, []);

  function handleLogout() {
    clearAdminToken();
    navigate("/admin/login", { replace: true });
  }

  async function loadAdminPosts() {
    setLoadingPosts(true);

    try {
      const posts = await fetchAdminBlogPosts();
      setBlogPosts(posts);
    } catch (error) {
      if (error?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      setMessage("We could not load the existing blog list right now.");
    } finally {
      setLoadingPosts(false);
    }
  }

  function updateSection(index, field, value) {
    setSections((current) =>
      current.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, [field]: value } : section,
      ),
    );
  }

  function addSection() {
    setSections((current) => [...current, createEmptySection()]);
  }

  function removeSection(index) {
    setSections((current) => current.filter((_, sectionIndex) => sectionIndex !== index));
  }

  function importJsonDraft(rawValue) {
    try {
      const parsed = JSON.parse(rawValue);
      setForm(applyJsonDraftToForm(parsed));
      setSections(normalizeImportedSections(parsed?.sections));
      setMessage("JSON draft imported. Review the fields and publish when ready.");
      setCreatedPost(null);
    } catch (error) {
      setMessage("That JSON draft could not be parsed. Please check the structure and try again.");
    }
  }

  function handleJsonImport() {
    if (!jsonDraft.trim()) {
      setMessage("Paste a JSON blog draft first, then import it into the editor.");
      return;
    }

    importJsonDraft(jsonDraft);
  }

  function handleJsonFileUpload(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const reader = new window.FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setJsonDraft(text);
      importJsonDraft(text);
    };
    reader.readAsText(file);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setMessage("");
    setCreatedPost(null);

    try {
      const payload = {
        title: form.title,
        seo_title: form.seo_title || null,
        seo_description: form.seo_description || null,
        excerpt: form.excerpt,
        template: form.template,
        category: form.category,
        hero_image: form.hero_image,
        hero_image_alt: form.hero_image_alt || form.title,
        supporting_gallery: form.supporting_gallery_text
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean),
        tags: form.tags_text
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
        read_time: form.read_time,
        author: form.author,
        featured: form.featured,
        published: form.published,
        sections: sections
          .filter((section) => section.title.trim() && section.content.trim())
          .map((section) => ({
            title: section.title.trim(),
            content: section.content.trim(),
            image_url: section.image_url.trim() || null,
            bullets: section.bullets_text
              .split("\n")
              .map((item) => item.trim())
              .filter(Boolean),
          })),
      };

      if (payload.sections.length === 0) {
        setMessage("Add at least one section with a title and content before publishing.");
        setSubmitting(false);
        return;
      }

      const created = await createBlogPost(payload);
      setCreatedPost(created);
      setMessage(
        created.published
          ? "Blog published successfully and added to the main website."
          : "Blog saved as an unpublished draft.",
      );
      setForm(initialForm);
      setSections([createEmptySection(), createEmptySection()]);
      setJsonDraft("");
      await loadAdminPosts();
    } catch (error) {
      if (error?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      setMessage("We could not publish that blog yet. Please review the form and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePublishToggle(post) {
    try {
      const updated = await updateAdminBlogPost(post.id, {
        published: !post.published,
      });

      setBlogPosts((current) =>
        current.map((item) => (item.id === updated.id ? updated : item)),
      );
      setMessage(
        updated.published
          ? "Blog published successfully."
          : "Blog moved back to unpublished drafts.",
      );
    } catch (error) {
      if (error?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      setMessage("We could not update the publish state for that blog.");
    }
  }

  async function handleDelete(post) {
    const confirmed = window.confirm(
      `Delete "${post.title}" permanently? This cannot be undone.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteAdminBlogPost(post.id);
      setBlogPosts((current) => current.filter((item) => item.id !== post.id));
      setMessage("Blog deleted successfully.");
    } catch (error) {
      if (error?.response?.status === 401) {
        clearAdminToken();
        navigate("/admin/login", { replace: true });
        return;
      }
      setMessage("We could not delete that blog.");
    }
  }

  const filteredBlogPosts = blogPosts.filter((post) => {
    if (postFilter === "published") {
      return post.published;
    }

    if (postFilter === "draft") {
      return !post.published;
    }

    return true;
  });

  return (
    <div className="blog-dashboard-page">
      <div className="blog-dashboard-shell">
        <section className="dashboard-hero-card">
          <div>
            <span className="dashboard-eyebrow">Publishing Dashboard</span>
            <h1>Add cornerstone blog content without editing code.</h1>
            <p>
              Add your article title, SEO text, hero image, supporting visuals,
              and repeatable listicle sections. As soon as you publish, the post
              appears on the public blog page automatically.
            </p>
          </div>
          <div className="dashboard-hero-actions">
            <Link to="/blogs" className="dashboard-view-link">
              View Public Blog
            </Link>
            <button type="button" className="dashboard-logout-button" onClick={handleLogout}>
              <LogOut size={16} />
              Log Out
            </button>
          </div>
        </section>

        {message && (
          <div className="dashboard-message-card">
            <CheckCircle2 size={20} />
            <div>
              <p>{message}</p>
              {createdPost && (
                <Link to={`/blogs/${createdPost.slug}`} className="dashboard-inline-link">
                  Open published article
                </Link>
              )}
            </div>
          </div>
        )}

        <section className="dashboard-panel">
          <div className="dashboard-panel-header dashboard-panel-header-space">
            <div className="dashboard-panel-title">
              <Eye size={18} />
              <h2>Manage Published And Unpublished Blogs</h2>
            </div>
            <div className="dashboard-filter-group">
              {[
                { id: "all", label: "All" },
                { id: "published", label: "Published" },
                { id: "draft", label: "Drafts" },
              ].map((filter) => (
                <button
                  type="button"
                  key={filter.id}
                  className={`dashboard-filter-button ${
                    postFilter === filter.id ? "dashboard-filter-button-active" : ""
                  }`}
                  onClick={() => setPostFilter(filter.id)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {loadingPosts ? (
            <p className="dashboard-helper-copy">Loading existing blog posts...</p>
          ) : filteredBlogPosts.length === 0 ? (
            <p className="dashboard-helper-copy">
              No blogs match the current filter yet.
            </p>
          ) : (
            <div className="dashboard-post-list">
              {filteredBlogPosts.map((post) => (
                <article className="dashboard-post-card" key={post.id}>
                  <div className="dashboard-post-meta">
                    <div>
                      <span
                        className={`dashboard-post-status ${
                          post.published ? "dashboard-post-status-live" : "dashboard-post-status-draft"
                        }`}
                      >
                        {post.published ? "Published" : "Unpublished"}
                      </span>
                      <strong>{post.title}</strong>
                      <p>
                        {post.category} • {getTemplateMeta(post.template).label} •{" "}
                        {formatDate(post.published_at)}
                      </p>
                    </div>
                    <div className="dashboard-post-actions">
                      {post.published ? (
                        <Link to={`/blogs/${post.slug}`} className="dashboard-inline-link">
                          Preview
                        </Link>
                      ) : (
                        <span className="dashboard-preview-disabled">Preview after publishing</span>
                      )}
                      <button
                        type="button"
                        className="dashboard-add-button"
                        onClick={() => handlePublishToggle(post)}
                      >
                        {post.published ? "Unpublish" : "Publish"}
                      </button>
                      <button
                        type="button"
                        className="dashboard-remove-button"
                        onClick={() => handleDelete(post)}
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="dashboard-panel">
          <div className="dashboard-panel-header dashboard-panel-header-space">
            <div className="dashboard-panel-title">
              <FileJson size={18} />
              <h2>Import AI Writer JSON</h2>
            </div>
            <label className="dashboard-file-upload">
              <Upload size={16} />
              Upload JSON File
              <input type="file" accept="application/json,.json" onChange={handleJsonFileUpload} />
            </label>
          </div>
          <p className="dashboard-helper-copy">
            Paste an API-ready JSON draft from your AI writer or upload a `.json`
            file. Supported fields include `title`, `excerpt`, `template`,
            `category`, `hero_image`, `supporting_gallery`, `tags`, `read_time`,
            `author`, `featured`, and `sections`.
          </p>
          <label className="dashboard-field dashboard-field-full">
            <span>Blog JSON Draft</span>
            <textarea
              value={jsonDraft}
              onChange={(event) => setJsonDraft(event.target.value)}
              className="dashboard-json-textarea"
              placeholder={`{
  "title": "**How to quiet racing thoughts** before sleep",
  "template": "editorial",
  "excerpt": "A gentle framework for calming the mind at night.",
  "category": "Sleep Support",
  "hero_image": "https://images.unsplash.com/...",
  "read_time": "7 min read",
  "sections": [
    {
      "title": "Start with the body",
      "content": "Use **slower breathing** and softer attention.",
      "bullets": ["Try a 4-6 breath cycle", "Relax the jaw first"]
    }
  ]
}`}
            />
          </label>
          <div className="dashboard-json-actions">
            <button type="button" className="dashboard-add-button" onClick={handleJsonImport}>
              Import JSON Into Editor
            </button>
          </div>
        </section>

        <form className="blog-dashboard-form" onSubmit={handleSubmit}>
          <section className="dashboard-panel">
            <div className="dashboard-panel-header">
              <FilePenLine size={18} />
              <h2>Article Basics</h2>
            </div>
            <div className="dashboard-grid">
              <label className="dashboard-field">
                <span>Blog Title</span>
                <input
                  value={form.title}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, title: event.target.value }))
                  }
                  placeholder="How to calm an anxious mind before sleep"
                  required
                />
              </label>

              <label className="dashboard-field">
                <span>Category</span>
                <input
                  value={form.category}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, category: event.target.value }))
                  }
                  placeholder="Mental Wellness"
                  required
                />
              </label>

              <label className="dashboard-field">
                <span>Read Time</span>
                <input
                  value={form.read_time}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, read_time: event.target.value }))
                  }
                  placeholder="8 min read"
                  required
                />
              </label>

              <label className="dashboard-field">
                <span>Author</span>
                <input
                  value={form.author}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, author: event.target.value }))
                  }
                  placeholder="FindClarity Team"
                />
              </label>

              <label className="dashboard-field dashboard-field-full">
                <span>Excerpt</span>
                <textarea
                  value={form.excerpt}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, excerpt: event.target.value }))
                  }
                  placeholder="A concise article summary used on the blog listing page and article hero."
                  required
                />
              </label>

              <p className="dashboard-formatting-note dashboard-field-full">
                Formatting supported in visible blog copy: `**bold**`, `*italic*`,
                `~~strike~~`, `` `inline code` ``, and links like
                `[label](https://example.com)`.
              </p>
            </div>
          </section>

          <section className="dashboard-panel">
            <div className="dashboard-panel-header">
              <FilePenLine size={18} />
              <h2>Choose a Design Template</h2>
            </div>
            <div className="dashboard-template-grid">
              {BLOG_TEMPLATES.map((template) => (
                <label
                  className={`dashboard-template-card ${
                    form.template === template.id ? "dashboard-template-card-active" : ""
                  }`}
                  key={template.id}
                >
                  <input
                    type="radio"
                    name="template"
                    value={template.id}
                    checked={form.template === template.id}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, template: event.target.value }))
                    }
                  />
                  <div>
                    <strong>{template.label}</strong>
                    <p>{template.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </section>

          <section className="dashboard-panel">
            <div className="dashboard-panel-header">
              <FilePenLine size={18} />
              <h2>SEO and Imagery</h2>
            </div>
            <div className="dashboard-grid">
              <label className="dashboard-field dashboard-field-full">
                <span>SEO Title</span>
                <input
                  value={form.seo_title}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, seo_title: event.target.value }))
                  }
                  placeholder="Optional. If blank, the blog title will be used."
                />
              </label>

              <label className="dashboard-field dashboard-field-full">
                <span>SEO Description</span>
                <textarea
                  value={form.seo_description}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      seo_description: event.target.value,
                    }))
                  }
                  placeholder="Optional. Short search-friendly summary."
                />
              </label>

              <label className="dashboard-field dashboard-field-full">
                <span>Hero Image URL</span>
                <input
                  value={form.hero_image}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, hero_image: event.target.value }))
                  }
                  placeholder="https://images.unsplash.com/..."
                  required
                />
              </label>

              <label className="dashboard-field dashboard-field-full">
                <span>Hero Image Alt Text</span>
                <input
                  value={form.hero_image_alt}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      hero_image_alt: event.target.value,
                    }))
                  }
                  placeholder="Calm room with soft evening light"
                />
              </label>

              <label className="dashboard-field dashboard-field-full">
                <span>Supporting Image URLs</span>
                <textarea
                  value={form.supporting_gallery_text}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      supporting_gallery_text: event.target.value,
                    }))
                  }
                  placeholder={"Add one image URL per line"}
                />
              </label>

              <label className="dashboard-field">
                <span>Tags</span>
                <input
                  value={form.tags_text}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, tags_text: event.target.value }))
                  }
                  placeholder="anxiety, sleep, self-care"
                />
              </label>

              <label className="dashboard-checkbox">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, featured: event.target.checked }))
                  }
                />
                <span>Mark as featured on the blog page</span>
              </label>

              <label className="dashboard-checkbox">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, published: event.target.checked }))
                  }
                />
                <span>Publish immediately after creating</span>
              </label>
            </div>
          </section>

          <section className="dashboard-panel">
            <div className="dashboard-panel-header dashboard-panel-header-space">
              <div className="dashboard-panel-title">
                <FilePenLine size={18} />
                <h2>Repeatable Blog Sections</h2>
              </div>
              <button type="button" className="dashboard-add-button" onClick={addSection}>
                <Plus size={16} />
                Add Section
              </button>
            </div>

            <div className="dashboard-sections-list">
              {sections.map((section, index) => (
                <div className="dashboard-section-card" key={`section-${index}`}>
                  <div className="dashboard-section-header">
                    <h3>Section {index + 1}</h3>
                    {sections.length > 1 && (
                      <button
                        type="button"
                        className="dashboard-remove-button"
                        onClick={() => removeSection(index)}
                      >
                        <Trash2 size={16} />
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="dashboard-grid">
                    <label className="dashboard-field dashboard-field-full">
                      <span>Section Title</span>
                      <input
                        value={section.title}
                        onChange={(event) =>
                          updateSection(index, "title", event.target.value)
                        }
                        placeholder="1. Recognize what your body is telling you"
                      />
                    </label>

                    <label className="dashboard-field dashboard-field-full">
                      <span>Section Content</span>
                      <textarea
                        value={section.content}
                        onChange={(event) =>
                          updateSection(index, "content", event.target.value)
                        }
                        placeholder="Write the main content for this section. You can use **bold** and *italic* styling."
                      />
                    </label>

                    <label className="dashboard-field dashboard-field-full">
                      <span>Section Image URL</span>
                      <input
                        value={section.image_url}
                        onChange={(event) =>
                          updateSection(index, "image_url", event.target.value)
                        }
                        placeholder="https://images.unsplash.com/..."
                      />
                    </label>

                    <label className="dashboard-field dashboard-field-full">
                      <span>Bullet Points</span>
                      <textarea
                        value={section.bullets_text}
                        onChange={(event) =>
                          updateSection(index, "bullets_text", event.target.value)
                        }
                        placeholder={"Add one bullet per line"}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <div className="dashboard-submit-row">
            <button type="submit" className="dashboard-submit-button" disabled={submitting}>
              {submitting ? "Publishing..." : "Publish Blog"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogDashboard;
